// Platform runtime byte caps — single source of truth for every cap that
// doesn't live in the deploy pipeline.
//
//   LLM_BODY_MAX_BYTES  — request body cap on /__mg/llm/* (front-door)
//   BLOB_PUT_MAX_BYTES  — object cap on PUT /__mg/platform/blob/<key>
//
// Deploy caps live in @mg/shared/deploy/validation (separate module — the
// wizard already imports from there for validateBundle()). Both feed into
// the GET /api/v1/limits endpoint so clients can pick up server-side bumps
// at runtime.

/** /__mg/llm/* request body cap. Front-door rejects larger bodies with 413. */
export const LLM_BODY_MAX_BYTES = 1 * 1024 * 1024;

/** PUT /__mg/platform/blob/<key> per-object cap. Front-door rejects larger
 *  uploads with 400. R2 itself supports far larger; this is the
 *  fetch-through cap. Future presigned-URL flow will lift this. */
export const BLOB_PUT_MAX_BYTES = 25 * 1024 * 1024;

/** Worker compatibility-date freshness window. Validated by
 *  workers/control-plane/src/validation/manifest.ts at deploy time to match
 *  Cloudflare's accepted range. Surfaced via platform_info so agents can size
 *  defaults without guessing. */
export const COMPAT_DATE_WINDOW_BACK_DAYS = 180;
export const COMPAT_DATE_WINDOW_AHEAD_DAYS = 30;

/** Cloudflare's hard cap on a deployed worker's compressed script size.
 *  Enforced upstream; we surface it here so agents can plan bundles. Bumps
 *  here are documentation-only until CF raises their cap. */
export const WORKER_SCRIPT_COMPRESSED_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Wire shape for the non-deploy half of GET /api/v1/limits. Versioned via
 * additive-only changes; existing fields never shrink in meaning.
 */
export interface PlatformRuntimeLimits {
  readonly llm: {
    readonly max_body_bytes: number;
  };
  readonly blob: {
    readonly max_put_bytes: number;
  };
}

export const PLATFORM_RUNTIME_LIMITS: PlatformRuntimeLimits = {
  llm: { max_body_bytes: LLM_BODY_MAX_BYTES },
  blob: { max_put_bytes: BLOB_PUT_MAX_BYTES },
};

// ---------------------------------------------------------------------------
// Namespace metadata limits — enforced by the Control Plane on
// POST/PATCH /api/v1/namespaces and mirrored as `maxlength` in the console.
// ---------------------------------------------------------------------------

/** Maximum length of a namespace's human-readable `display_name` (after trimming). */
export const NAMESPACE_DISPLAY_NAME_MAX = 80;

/** Maximum length of a namespace's `description` (after trimming). */
export const NAMESPACE_DESCRIPTION_MAX = 500;

// ---------------------------------------------------------------------------
// MCP app-kind limits — see docs/specs/2026-05-18-mcp-app-kind.md §14.
// ---------------------------------------------------------------------------

/** Maximum number of tools an MCP app may declare. Enforced by validateMcpManifest. */
export const MCP_TOOLS_PER_APP_MAX = 64;

/** Maximum length of a tool's `name`. Matches the MCP_TOOL_NAME pattern in the validator. */
export const MCP_TOOL_NAME_MAX = 64;

/** Maximum length of a tool's `description`. */
export const MCP_TOOL_DESCRIPTION_MAX = 2000;

/**
 * Wall-clock budget for a single tools/call dispatch through the MCP gateway.
 * Chosen to stay well under MCP clients' "tool stuck" UX timeouts.
 */
export const MCP_TOOL_CALL_TIMEOUT_MS = 30_000;

/** Cap on a tools/call result body (after JSON serialization). */
export const MCP_TOOL_RESULT_MAX_BYTES = 1_000_000;

/**
 * Per-(caller, app) rate limit for MCP tools/call. Applied at the gateway via
 * CF Rate Limiting binding. Initialize / tools/list are not rate-limited.
 * Tunable — instrument before changing.
 */
export const MCP_RATE_LIMIT_PER_MINUTE = 100;
